/**
 * The "because…" line and the deferred-cost note for the way a decision actually moved (content §2.6). The catalog carries a sentence for each
 * direction (`deferredNotes`, "up" = toward the later option: Lead, Longer, Faster, Looser, Larger); an entry without them gives its one
 * `deferredCostNote`, written for the cost-bearing direction, whichever way it moved.
 */
import type { AlmSnapshot, Decisions } from "@bsim/domain";
import type { DecisionCatalogEntry } from "@bsim/contracts";
import { readDecision, sameValue } from "./decisionValues";
import { lowerFirst } from "../format/labels";

/** What the plan trades against the position the round opens with: a pay-fixed swap added, a receive-fixed swap added, or a swap ended. */
export type SwapTrade = "payFixed" | "receiveFixed" | "reduced" | "terminated";
/** A fall in the pay-fixed position that stays at or above zero only trims the hedge held; it is not a receive-fixed trade, so the catalog's "receives fixed" sentence never applies. */
export const REDUCED_HEDGE_NOTE = "This reduces the hedge: less protection against a rate rise, and less cost if rates fall.";
type Swaps = NonNullable<AlmSnapshot["swaps"]>;
const net = (swaps: Swaps): number => swaps.reduce((sum, s) => sum + (s.direction === "payFixed" ? s.notionalUsd : -s.notionalUsd), 0);

/** The trade from the swaps held at the round's start and the swaps the plan holds at year-end: the net pay-fixed change decides; a swap gone with none added is an ending; null when nothing trades. */
export function swapTradeOf(start: Swaps | undefined, plan: Swaps | undefined, yearEndMonth: number): SwapTrade | null {
  if (start === undefined || plan === undefined) return null;
  // A swap that matures inside the year runs off by itself: it is neither an ending the player chose nor part of the position the plan trades from.
  const live = start.filter((s) => s.maturityMonth > yearEndMonth);
  const added = plan.some((p) => !start.some((s) => s.id === p.id));
  const gone = live.some((s) => !plan.some((p) => p.id === s.id));
  if (gone && !added) return "terminated";
  const change = net(plan) - net(live);
  return change > 0 ? "payFixed" : change < 0 ? (net(plan) >= 0 && net(live) > 0 ? "reduced" : "receiveFixed") : null;
}

/** A decision that sets a swap tenor is the one that trades swaps; its direction is the trade's, never the order of its options. */
export const tradesSwaps = (entry: DecisionCatalogEntry): boolean => entry.options.tenorYears !== undefined;

/**
 * The note for the way `entry` moved from `was` to `now`; null when it did not move. A swap-trading decision reads the trade (`trade`: undefined while the preview has not said, null for no trade):
 * its "up" note is for a pay-fixed trade, "down" for receive-fixed, the entry's one note for an ended swap or while the trade is not known.
 */
export function deferredNote(entry: DecisionCatalogEntry, now: Decisions, was: Partial<Decisions>, trade?: SwapTrade | null): string | null {
  if (tradesSwaps(entry) && entry.deferredNotes !== undefined) {
    const moved = Object.keys(entry.options).some((sub) => { const [a, b] = [readDecision(was, entry.decisionId, sub), readDecision(now, entry.decisionId, sub)]; return a !== undefined && b !== undefined && !sameValue(a, b); });
    if (!moved || trade === null) return null;
    return trade === "payFixed" ? entry.deferredNotes.up : trade === "receiveFixed" ? entry.deferredNotes.down : trade === "reduced" ? REDUCED_HEDGE_NOTE : entry.deferredCostNote;
  }
  let moved = false;
  for (const [sub, options] of Object.entries(entry.options)) {
    const before = readDecision(was, entry.decisionId, sub);
    const after = readDecision(now, entry.decisionId, sub);
    if (before === undefined || after === undefined || sameValue(before, after)) continue;
    moved = true;
    const from = options.findIndex((o) => sameValue(o.value, before));
    const to = options.findIndex((o) => sameValue(o.value, after));
    if (entry.deferredNotes !== undefined && from >= 0 && to >= 0) return to > from ? entry.deferredNotes.up : entry.deferredNotes.down;
  }
  return moved ? entry.deferredCostNote : null;
}

/** "lagging saves cost now; …": a note as the tail of "because …". */
export const asBecause = (note: string): string => lowerFirst(note).replace(/\.$/, "");
